/**
 * Routing — about fifty lines instead of a library.
 *
 * The requirement is small: the address bar must name what you are looking at,
 * so a project can be bookmarked, refreshed, and pasted into a message to a
 * colleague. The server already sends any non-API path to the app shell (see
 * `serveWebApp` in the API), so a deep link survives a refresh.
 *
 * `history.pushState` fires no event of its own — only Back and Forward do —
 * so `navigate` announces its own change. That is the whole trick here.
 */

import { useEffect, useState } from 'react';

const CHANGED = 'fitout:navigate';

/** Where we are, path and query together — what `navigate` is compared against. */
function here(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/**
 * Move to `to` and tell every listening component.
 *
 * `to` may carry a query string. It is compared against the path AND the query
 * because a cross-link differs from where you are standing only in its
 * `?focus=` — comparing paths alone would make "show me that material" a no-op
 * when you are already on the material register.
 */
export function navigate(to: string): void {
  if (to === here()) return;
  window.history.pushState({}, '', to);
  window.dispatchEvent(new Event(CHANGED));
}

/** The current path, re-rendering the caller when it changes. */
export function usePath(): string {
  return useLocation().path;
}

/** The current path and query, re-rendering the caller when either changes. */
export function useLocation(): { readonly path: string; readonly search: string } {
  const [at, setAt] = useState(() => ({
    path: window.location.pathname,
    search: window.location.search,
  }));

  useEffect(() => {
    const sync = (): void =>
      setAt((was) =>
        // Same values, same object: a new object every event would re-render
        // every section on the page each time anything navigated.
        was.path === window.location.pathname && was.search === window.location.search
          ? was
          : { path: window.location.pathname, search: window.location.search },
      );
    // The listeners are attached AFTER the first render, so a navigation that
    // happens in between would be missed. Syncing once on mount closes it.
    sync();
    // popstate = the browser's own Back/Forward. CHANGED = our own navigate().
    window.addEventListener('popstate', sync);
    window.addEventListener(CHANGED, sync);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener(CHANGED, sync);
    };
  }, []);

  return at;
}

/** A project path: which project, which section, and which record inside it. */
export interface ProjectRoute {
  readonly id: string;
  /** The section key in `/projects/<id>/<section>`, or null for the overview. */
  readonly section: string | null;
  /**
   * The register in `/projects/<id>/<section>/<register>/<record>`, or null.
   *
   * WHY THE REGISTER IS IN THE ADDRESS AND NOT ONLY THE RECORD
   * ----------------------------------------------------------
   * A section holds more than one register — `design` holds drawings AND
   * submittals — so `/projects/<id>/design/<uuid>` cannot say which of the two
   * the id belongs to without asking the server for both and seeing which one
   * answers. Naming the register makes the address self-describing, matches the
   * `{ section, register }` pair the gate targets already model, and gives the
   * record page a back link it can derive rather than remember: its own address
   * with the last two segments removed.
   */
  readonly register: string | null;
  /** The record in that register. Always null when `register` is null. */
  readonly recordId: string | null;
}

/**
 * `/projects/<id>`, `/projects/<id>/<section>` and
 * `/projects/<id>/<section>/<register>/<record>` — null anywhere else.
 *
 * The project page carries twenty-three registers. They are grouped into
 * sections (`pages/project-sections.ts`) and the section is IN THE ADDRESS BAR,
 * for the same reason the project id is: a colleague asking "where do I record
 * a delivery?" can be sent a link that opens on the right screen, rather than
 * an instruction to scroll. A record page is that same argument one level down.
 *
 * THE RECORD GROUP IS NESTED INSIDE THE SECTION GROUP, and that nesting is the
 * whole correctness of this regex. Written as two independent optional groups,
 * `/projects/<id>/site/diary` backtracks into "no section, register `site`,
 * record `diary`" — a three-segment path silently read as a record page. Nested,
 * a register cannot exist without the section it lives in, and that path is
 * refused exactly as it always was.
 */
export function projectRouteFrom(path: string): ProjectRoute | null {
  const match = /^\/projects\/([^/]+)(?:\/([^/]+)(?:\/([^/]+)\/([^/]+))?)?\/?$/.exec(path);
  if (!match?.[1]) return null;
  return {
    id: decodeURIComponent(match[1]),
    section: match[2] ? decodeURIComponent(match[2]) : null,
    register: match[3] ? decodeURIComponent(match[3]) : null,
    recordId: match[4] ? decodeURIComponent(match[4]) : null,
  };
}

/**
 * The project id in `/projects/<id>` — the OVERVIEW path only.
 *
 * Deliberately narrow: a caller asking this question wants to know it is the
 * project's own page rather than one of its sections. Anything that has to work
 * on both asks `projectRouteFrom`.
 */
export function projectIdFrom(path: string): string | null {
  const route = projectRouteFrom(path);
  return route && route.section === null ? route.id : null;
}

/**
 * The token in `/invite/<companyId>.<token>`, or null anywhere else.
 *
 * One of TWO routes that work without a session — the person following it has
 * no account yet — so it is matched before anything asks who they are.
 */
export function inviteTokenFrom(path: string): string | null {
  const match = /^\/invite\/([^/]+)\/?$/.exec(path);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * The token in `/reset/<companyId>.<token>`, or null anywhere else.
 *
 * The other session-less route, and for a stronger reason: somebody following
 * this one cannot sign in, which is the entire point of the link.
 */
export function resetTokenFrom(path: string): string | null {
  const match = /^\/reset\/([^/]+)\/?$/.exec(path);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}
